import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Star, MessageSquare, Truck, Loader2, CheckCircle, Clock, Send, X
} from 'lucide-react';
import axios from 'axios';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { useClientPermissions } from '../hooks/useClientPermissions';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRating = ({ value, onChange, label, disabled }) => (
  <div className="flex items-center gap-3">
    <span className="text-sm text-gray-600 w-32">{label}</span>
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type="button"
          onClick={() => !disabled && onChange(i)}
          className={cn("transition-colors", disabled ? "cursor-default" : "cursor-pointer hover:scale-110")}
          disabled={disabled}
          data-testid={`star-${label.toLowerCase().replace(/\s/g, '-')}-${i}`}
        >
          <Star className={cn("w-6 h-6", i <= value ? "fill-amber-400 text-amber-400" : "text-gray-300")} />
        </button>
      ))}
    </div>
    <span className="text-sm font-medium text-gray-500">{value}/5</span>
  </div>
);

const ClientFeedback = () => {
  const permissions = useClientPermissions();
  const [pending, setPending] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeOp, setActiveOp] = useState(null);
  const [formData, setFormData] = useState({ global: 0, punctuality: 0, condition: 0, comment: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, feedbacksRes] = await Promise.all([
        axios.get(`${API}/client/feedbacks/pending`),
        axios.get(`${API}/client/feedbacks`)
      ]);
      setPending(pendingRes.data.pending || []);
      setFeedbacks(feedbacksRes.data.feedbacks || []);
    } catch (err) {
      console.error('Error fetching feedbacks:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openForm = (op) => {
    setActiveOp(op);
    setFormData({ global: 0, punctuality: 0, condition: 0, comment: '' });
  };

  const submitFeedback = async () => {
    if (!activeOp) return;
    if (formData.global === 0 || formData.punctuality === 0 || formData.condition === 0) {
      toast.error('Veuillez remplir toutes les notes');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/client/feedbacks`, {
        operationId: activeOp.id,
        globalRating: formData.global,
        punctualityRating: formData.punctuality,
        merchandiseConditionRating: formData.condition,
        comment: formData.comment || null
      });
      try { (await import('../utils/analytics')).trackFeature('feedback.submit', { global: formData.global }); } catch (_) {}
      toast.success('Merci pour votre feedback !');
      setActiveOp(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6" data-testid="client-feedback-page">
      <div>
        <h1 className="text-xl md:text-3xl font-bold text-gray-900">Évaluations</h1>
        <p className="text-gray-500 mt-1">Donnez votre avis sur vos opérations terminées</p>
      </div>

      {/* Pending feedbacks */}
      {pending.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30" data-testid="pending-feedbacks">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="w-5 h-5 text-amber-600" />
              {pending.length} opération{pending.length > 1 ? 's' : ''} en attente d'évaluation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pending.map(op => (
              <div key={op.id} className="flex items-center justify-between p-3 bg-white rounded-lg border" data-testid={`pending-${op.id}`}>
                <div>
                  <p className="font-medium text-gray-900">
                    {op.departure} → {op.arrival}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                    {op.operationNumber && <span>{op.operationNumber}</span>}
                    <span>Livré le {formatDate(op.completedAt || op.deliveryDate)}</span>
                  </div>
                </div>
                {!permissions.isReadOnly && (
                  <Button size="sm" onClick={() => openForm(op)} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid={`rate-btn-${op.id}`}>
                    <Star className="w-4 h-4 mr-1" /> Évaluer
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Feedback Form Modal */}
      {activeOp && (
        <Card className="border-[#1db584] border-2" data-testid="feedback-form">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                Évaluer : {activeOp.departure} → {activeOp.arrival}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setActiveOp(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-gray-500">{activeOp.operationNumber}</p>
          </CardHeader>
          <CardContent className="space-y-5">
            <StarRating label="Note globale" value={formData.global} onChange={(v) => setFormData(f => ({ ...f, global: v }))} />
            <StarRating label="Ponctualité" value={formData.punctuality} onChange={(v) => setFormData(f => ({ ...f, punctuality: v }))} />
            <StarRating label="État marchandise" value={formData.condition} onChange={(v) => setFormData(f => ({ ...f, condition: v }))} />
            <div>
              <label className="text-sm text-gray-600 block mb-1.5">Commentaire (optionnel)</label>
              <textarea
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1db584]"
                rows={3}
                placeholder="Partagez votre expérience..."
                value={formData.comment}
                onChange={(e) => setFormData(f => ({ ...f, comment: e.target.value }))}
                data-testid="feedback-comment"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setActiveOp(null)}>Annuler</Button>
              <Button onClick={submitFeedback} disabled={submitting} className="bg-[#1db584] hover:bg-[#16a075]" data-testid="submit-feedback-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                Envoyer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Past feedbacks */}
      <Card data-testid="past-feedbacks">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-gray-400" />
            Mes évaluations ({feedbacks.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {feedbacks.length === 0 ? (
            <div className="text-center py-8">
              <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucune évaluation soumise</p>
            </div>
          ) : (
            <div className="space-y-3">
              {feedbacks.map(fb => (
                <div key={fb.id} className="p-4 bg-gray-50 rounded-lg" data-testid={`feedback-${fb.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{fb.operationNumber || fb.operationId?.slice(0, 8)}</span>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(i => (
                        <Star key={i} className={cn("w-4 h-4", i <= fb.avgRating ? "fill-amber-400 text-amber-400" : "text-gray-300")} />
                      ))}
                      <span className="text-sm font-medium ml-1">{fb.avgRating}</span>
                    </div>
                  </div>
                  <div className="flex gap-4 text-xs text-gray-500 mb-2">
                    <span>Global: {fb.globalRating}/5</span>
                    <span>Ponctualité: {fb.punctualityRating}/5</span>
                    <span>Marchandise: {fb.merchandiseConditionRating}/5</span>
                  </div>
                  {fb.comment && <p className="text-sm text-gray-600 italic">"{fb.comment}"</p>}
                  <p className="text-xs text-gray-400 mt-1">{formatDate(fb.createdAt)}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientFeedback;
